import { useEffect, useState } from 'react';
import useGameStore, { sellQuoteOf } from '../store/gameStore';
import type { PropertyTile } from '../types/gameTypes';
import { money } from '../lib/format';
import { DISTRICT_META } from './districtMeta';

/** Two-way slider modal: buy from float / sell owned shares back to the pool. */
export const TradeSharesModal = ({ tile, mode, onClose }: { tile: PropertyTile | null; mode: 'BUY' | 'SELL'; onClose: () => void }) => {
  const store = useGameStore();
  const active = store.players[store.currentPlayerIndex];
  // Hotseat: buying/selling belongs to the human at the controls (during their turn only).
  const human = active && !active.isAI ? active : store.players[0];
  const canAct = Boolean(active && !active.isAI);
  const [qty, setQty] = useState(10);
  const meta = tile ? DISTRICT_META[tile.district] : null;
  const float = tile ? Math.max(0, tile.totalShares - Object.values(tile.shares).reduce((s, v) => s + v, 0)) : 0;
  const owned = tile && human ? (tile.shares[human.id] ?? 0) : 0;
  const unit = tile ? (tile.valuation > 0 ? tile.valuation : tile.baseCost) / Math.max(1, tile.totalShares) : 0;

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [onClose]);
  useEffect(() => { setQty(mode === 'SELL' ? owned : 10); }, [tile?.id, mode]); // eslint-disable-line

  if (!tile || !meta || !human) return null;
  const isSell = mode === 'SELL';
  // Strict acquisition rule: the public float is buyable only when standing on the
  // tile or when already holding a >50% majority of the issued shares.
  const standingHere = tile.id === (store.board[active?.currentTileIndex ?? -1]?.id ?? null);
  const pct = (owned / Math.max(1, tile.totalShares)) * 100;
  const canBuy = standingHere || pct > 50;
  const max = isSell ? owned : Math.min(float, Math.floor(human.cash / Math.max(1, unit)), 100);
  const q = Math.min(qty, Math.max(0, max));
  const quote = sellQuoteOf(tile, q);
  const gross = Math.round(q * unit);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/60 bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className={`h-2.5 w-full ${isSell ? 'bg-rose-400' : ''}`} style={isSell ? undefined : { backgroundColor: meta.bar }} />
        <div className="p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: meta.bar }}>{meta.label}</p>
          <h3 className="mt-1 text-lg font-extrabold text-white">{isSell ? 'Sell Shares · ' : 'Buy Shares · '}{tile.name}</h3>
          <input type="range" min={0} max={Math.max(0, max)} value={q} onChange={(e) => setQty(Number(e.target.value))} className={`mt-4 w-full ${isSell ? 'accent-rose-400' : 'accent-cyan-400'}`} />
          <div className="mt-3 space-y-1 text-sm">
            <p className="text-slate-200">Selected Shares: <b>{q}</b> / {max} max</p>
            <p className="text-slate-400">Price per Share: <b className="text-slate-100">{money(unit)}</b></p>
            {isSell ? (
              <>
                <p className="text-slate-200">Gross Payout: <b className="text-amber-300">{money(quote.gross)}</b></p>
                <p className="text-slate-400">Exit Fee 5%: <b className="text-rose-300">−{money(quote.gross - quote.net)}</b></p>
                <p className="text-slate-200">Net Proceeds: <b className="text-emerald-300">{money(quote.net)}</b></p>
                <p className="text-slate-400">Cash After: <b className="text-emerald-300">{money(human.cash + quote.net)}</b></p>
              </>
            ) : (
              <>
                <p className="text-slate-200">Total Investment: <b className="text-amber-300">{money(gross)}</b></p>
                <p className="text-slate-400">Cash Remaining: <b className={human.cash - gross < 0 ? 'text-rose-300' : 'text-emerald-300'}>{money(human.cash - gross)}</b></p>
              </>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            {isSell ? (
              <button disabled={!canAct || q <= 0} onClick={() => { store.sellShares(human.id, tile.id, q); onClose(); }} className="flex-1 rounded-xl bg-gradient-to-r from-rose-400 to-orange-400 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500">Sell · {money(quote.net)}</button>
            ) : (
              <button disabled={!canAct || q <= 0 || gross > human.cash || !canBuy} onClick={() => { store.addShares(human.id, tile.id, q, unit); store.clearOffer(); onClose(); }} className="flex-1 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-300 px-4 py-2.5 text-sm font-extrabold text-slate-950 disabled:from-slate-700 disabled:to-slate-700 disabled:text-slate-500">Buy · {money(gross)}</button>
            )}
            <button onClick={onClose} className="rounded-xl bg-slate-800 px-4 py-2.5 text-xs font-semibold text-slate-300">Cancel</button>
          </div>
          {!isSell && !canBuy && (
            <p className="mt-2 rounded-lg border border-amber-400/40 bg-amber-500/10 px-2.5 py-1.5 text-[11px] font-semibold text-amber-200">
              Can only acquire shares when standing on this tile or holding &gt;50% majority ownership (otherwise acquire via P2P Trade or Auction)
            </p>
          )}
          {max <= 0 && <p className="mt-2 text-xs text-rose-300">{isSell ? 'You own no shares here.' : 'No affordable float shares.'}</p>}
        </div>
      </div>
    </div>
  );
};
